import { Box } from '@mui/material';
import { useTranslation } from 'next-i18next';
import type { FC } from 'react';
import React, { useRef, useState } from 'react';
import type { GroupLoadBalancingData } from 'utils/common/loadBalancingHelpers';
import { generateGroupID } from 'utils/common/loadBalancingHelpers';

import ScrollContainerWrapper from 'components/DeviceConfigComponents/ScrollContainerWrapper';
import ModalHeader from 'components/Modal/ModalHeader';

import CreateLoadBalancingGroup from './components/CreateLoadBalancingGroup';
import ExistingLoadBalancingGroups from './components/ExistingLoadBalancingGroups';

export interface SetupLoadBalancingProps {
  loadBalancingGroupId: string;
  loadBalancingEnabled: boolean;
  maxTotalCurrent: number;
  maxGridCurrent: number;
  loadBalancingPriority?: number;
  loadBalancingFallbackCurrent?: number;
  phaseAssignment?: string;
  isGroupConfig: boolean;
  setLoadBalancingGroupId: (value: string) => void;
  setLoadBalancingEnabledChange: (value: boolean) => void;
  setMaxTotalCurrent: (value: number) => void;
  setMaxGridCurrent: (value: number) => void;
  setLoadBalancingPriority?: (value: number) => void;
  setPhaseAssignment?: (value: string) => void;
  setLoadBalancingFallbackCurrent?: (value: number) => void;
  setInputErrors: (value: boolean) => void;
  handleSubmitLoadBalancingSetup: (
    event: React.SyntheticEvent<HTMLFormElement>,
  ) => void;
}

type View = 'list' | 'create';

const SetupLoadBalancing: FC<SetupLoadBalancingProps> = props => {
  const {
    loadBalancingGroupId,
    setLoadBalancingGroupId,
    setMaxTotalCurrent,
    setMaxGridCurrent,
  } = props;

  const { t } = useTranslation();

  const [view, setView] = useState<View>('list');
  const [isEdit, setIsEdit] = useState(false);
  const previousGroupId = useRef('');

  const handleCreateGroup = (): void => {
    previousGroupId.current = loadBalancingGroupId;
    setLoadBalancingGroupId(generateGroupID());
    setIsEdit(false);
    setView('create');
  };

  const handleSelectGroup = (values: GroupLoadBalancingData): void => {
    setLoadBalancingGroupId(values.loadBalancingGroupId);

    if (values.maxTotalCurrent !== undefined) {
      setMaxTotalCurrent(values.maxTotalCurrent);
    }

    if (values.maxGridCurrent !== undefined) {
      setMaxGridCurrent(values.maxGridCurrent);
    }
  };

  const handleEditGroup = (values: GroupLoadBalancingData): void => {
    previousGroupId.current = loadBalancingGroupId;
    handleSelectGroup(values);
    setIsEdit(true);
    setView('create');
  };

  return (
    <ScrollContainerWrapper height="65vh">
      <ModalHeader
        title={
          view === 'create'
            ? t('common:pages.load_balancing.add_load_balancing_group.title')
            : t('common:pages.settings.subpages.load_balancing.title')
        }
      />
      <Box display="flex" flexDirection="column" overflow="auto">
        {view === 'create' ? (
          <CreateLoadBalancingGroup {...props} isEdit={isEdit} />
        ) : (
          <ExistingLoadBalancingGroups
            selectedGroupId={loadBalancingGroupId}
            onSelectGroup={handleSelectGroup}
            onCreateGroup={handleCreateGroup}
            onEditGroup={handleEditGroup}
          />
        )}
      </Box>
    </ScrollContainerWrapper>
  );
};

export default SetupLoadBalancing;
